import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, filter, fromEvent, map, merge, switchMap, tap, timer, withLatestFrom } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { IconComponent } from '@kin/spark-ui';
import { SignupHeaderComponent } from './ui/signup-header.component';
import { SavingsBannerComponent } from './address/savings-banner.component';
import { PropertyConfirmComponent } from './address/property-confirm.component';
import { EnrollFormComponent } from './enroll/enroll-form.component';
import { WaitlistFormComponent } from './waitlist/waitlist-form.component';
import { SignupCardComponent } from './ui/signup-card.component';
import { SignupAddressStore } from './address/signup-address.store';
import { SignupAddressService } from './address/signup-address.service';
import { SignupResetService } from './signup-reset.service';
import { SignupLoaderComponent } from './ui/signup-loader.component';
import { SignupEnrollmentService } from './enroll/signup-enrollment.service';
import { SignupErrorComponent } from './errors/signup-error.component';
import { TcpaBrowserMetadataService } from './intake/tcpa-browser-metadata.service';
import { ENROLLED_STATE, SubmittedSignupService } from './submitted/submitted-signup.service';
import { SubmittedNoticeComponent } from './submitted/submitted-notice.component';

export const LOOKUP_ERROR_MESSAGE = 'There was a problem, please try again';

/** How long the blurred, submitted page (enrollment or waitlist) holds before /enrolled replaces it. */
export const ENROLLED_BLUR_MS = 400;

/**
 * Public sign-up page (step 2 of 2 after the address-first landing page).
 *
 * Kin brand tokens come from signup-theme.css (shared with /enrolled).
 */
@Component({
  selector: 'app-signup',
  standalone: true,
  imports: [
    IconComponent, SignupHeaderComponent, SavingsBannerComponent, PropertyConfirmComponent,
    EnrollFormComponent, WaitlistFormComponent, SignupCardComponent, SignupLoaderComponent, SignupErrorComponent,
    SubmittedNoticeComponent,
  ],
  template: `
    <app-signup-header />

    <!-- Nothing below renders until the querystring address has been through the lookup -->
    @if (store.isLookingUp()) {
      <main class="main narrow">
        <app-signup-card>
          <app-signup-loader>Processing your address…</app-signup-loader>
        </app-signup-card>
      </main>
    } @else if (store.lookupStatus() === 'error') {
      <!-- The lookup failed (a 429 goes to /too-many-requests instead): Back returns to the landing page to retry -->
      <main><app-signup-error [message]="lookupErrorMessage" [back]="true" /></main>
    } @else {
      <!-- Back from /enrolled: the submitted page stays frozen below; this says so and offers the way on -->
      @if (restored) {
        <app-submitted-notice [class.narrow]="!store.isServiceable()" />
      }
      <!-- Frozen while leaving (the stores clear on pagehide, so nothing re-renders before the landing page
           paints), and once submitted: the whole page, until /enrolled renders, and again when Back restores it -->
      <main class="main" [class.narrow]="!store.isServiceable()" [class.leaving]="reset.leaving()"
            [class.frozen]="submitted() || restored" [class.restored]="restored"
            [attr.inert]="reset.leaving() || submitted() || restored ? '' : null"
            [attr.aria-busy]="reset.leaving() || submitted() || null">
        <div class="progress">
          <a class="back" [href]="reset.landingUrl" (click)="reset.onLandingLinkClick($event)">
            <ngk-icon icon="arrow-left" size="16px" />Back
          </a>
          @if (store.isServiceable()) {
            <span>·</span>
            <span class="step">{{ stepLabel }}</span>
            <span>·</span>
            <span>{{ stepHint }}</span>
          }
        </div>

        @if (store.isServiceable()) {
          <app-savings-banner />
          <!-- A new answer updates Form 50-162 like a corrected input (debounced) -->
          <app-property-confirm #confirm [locked]="enrollment.locksContact()" (ownerRelChange)="enroll.onChange()" />
          <!-- Frozen while "None of these apply": only the owner (or their agent) can enroll -->
          <app-enroll-form #enroll [class.frozen]="confirm.notOwner()"
                           [attr.inert]="confirm.notOwner() ? '' : null"
                           [attr.aria-disabled]="confirm.notOwner() || null"
                           (ownerRelRequired)="confirm.requireAnswer()" (submitted)="onSubmitted()" />

          <div class="trust">
            <div><ngk-icon icon="shield-check" size="16px" />Licensed in Texas</div>
            <div><ngk-icon icon="lock" size="16px" />Secure</div>
          </div>
        } @else {
          <app-waitlist-form (submitted)="onSubmitted()" />
        }
      </main>
    }
  `,
  styleUrl: './signup-theme.css',
  styles: `
    .main { max-width: 720px; margin: 0 auto; padding: 40px 24px 72px; }
    .main.narrow { max-width: 640px; }

    /*
     * Frozen (inputs are inert): dim + soften. The whole page while leaving for the landing page, until it
     * paints, and once the form is submitted, so it can't be submitted again; and the enroll form while the
     * visitor says they aren't the owner. Restored (Back from /enrolled), it sits under the submitted notice.
     */
    .main, .frozen { transition: opacity 120ms var(--kin-ease-out), filter 120ms var(--kin-ease-out); }
    .main.leaving, .frozen { opacity: 0.55; filter: blur(2px); pointer-events: none; }
    .frozen { display: block; }
    .main.restored { padding-top: 24px; }
    :host:has(.main.leaving) { cursor: progress; }
    @media (prefers-reduced-motion: reduce) {
      .main, .frozen { transition: none; }
      .main.leaving, .frozen { filter: none; }
    }

    .progress { display: flex; align-items: center; gap: 10px; font-size: 14px; color: var(--kin-text-tertiary); margin-bottom: 20px; }
    .progress a.back {
      display: inline-flex; align-items: center; gap: 6px;
      color: var(--kin-text-secondary); text-decoration: none; font-weight: 600;
    }
    .step { font-weight: 700; color: var(--kin-text); }


    .trust {
      display: flex; justify-content: center; gap: 40px; flex-wrap: wrap; margin: 32px auto 0;
      font-size: 12px; font-weight: 600; color: var(--kin-text-tertiary);
    }
    .trust div { display: flex; align-items: center; gap: 8px; }

    @media (max-width: 600px) {
      .main { padding: 24px 16px 56px; }
    }
  `,
})
export class SignupComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private addressService = inject(SignupAddressService);
  private destroyRef = inject(DestroyRef);
  protected reset = inject(SignupResetService);
  protected store = inject(SignupAddressStore);
  protected enrollment = inject(SignupEnrollmentService);
  private submittedSignup = inject(SubmittedSignupService);

  /** Back from /enrolled: this history entry is the submitted page, shown frozen as it was (see SubmittedSignupService). */
  protected readonly restored = this.submittedSignup.restore();

  constructor() {
    // Before the first render and the address lookup: the TCPA consent records the browser as the page opened
    if (!this.restored) inject(TcpaBrowserMetadataService).capture();
  }

  protected readonly lookupErrorMessage = LOOKUP_ERROR_MESSAGE;

  // Step navigation: /signup is the flow's second and last step
  stepLabel = 'Step 2 of 2';
  stepHint = 'Almost done';

  // Post-submit (enrolling or joining the waitlist): the page blurs, then /enrolled opens; Back returns to it blurred
  protected readonly submitted = signal(false);

  protected onSubmitted(): void {
    this.submitted.set(true);
    // Kept on this history entry before /enrolled clears the stores, so Back shows the submitted page, not a new form
    this.submittedSignup.save();
    timer(ENROLLED_BLUR_MS).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.router.navigate(['/enrolled'], { state: { [ENROLLED_STATE]: true } }));
  }

  ngOnInit(): void {
    // After leaving, the browser may restore this page from the back/forward cache with the stores already
    // cleared on pagehide; the page thaws, and each branch below fills the stores again.
    const restoredAfterLeaving = fromEvent<PageTransitionEvent>(window, 'pageshow').pipe(
      filter(e => e.persisted && this.reset.leaving()),
      tap(() => this.reset.leaving.set(false)),
    );
    // Restored for display only: no lookup (it would save a new intake), and the stores clear again on leaving.
    // Back to it from the landing page ("Enter another address") puts the submitted page back from this history entry.
    if (this.restored) {
      this.destroyRef.onDestroy(() => this.reset.reset());
      restoredAfterLeaving.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.submittedSignup.restore());
      return;
    }
    // ?street=…&unit=…&city=…&state=…&zip=… (unit optional). The query param map emits synchronously, so the
    // address is stored and the lookup is in flight (→ loading state) before the first render. A param change
    // cancels the in-flight lookup and starts a new one. Restored after leaving: re-run the lookup for the current params.
    const paramsAfterLeaving = restoredAfterLeaving.pipe(
      withLatestFrom(this.route.queryParamMap),
      map(([, params]) => params),
    );
    merge(this.route.queryParamMap, paramsAfterLeaving).pipe(
      switchMap(params => {
        const address = this.store.setFromQueryParams(params);
        if (address) return this.addressService.lookupAddress(address);
        return EMPTY;
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }
}
